/** Уведомления: короткая карточка сверху при входе.
 *
 * Форма взята у системных уведомлений телефона — там она уже понятна: приехало
 * сверху, полежало, ушло, можно смахнуть. Материал наш: то же запотевшее
 * стекло и та же мята, поэтому карточка читается как часть приложения, а не
 * как всплывшее чужое окно.
 *
 * Тап и смахивание делают разное, и это важно. Смахнул вверх — убрал, больше
 * не мешает. Нажал — открыл целиком: длинный текст в карточку не помещается,
 * и обрывать его на полуслове без возможности дочитать было бы издевательством.
 */
import { useEffect, useRef, useState } from 'react';
import { request } from '@/shared/api';
import { renderText } from '@/shared/richtext';
import { tap } from '@/shared/tg';

export interface Notice {
  id: string;
  icon: string;
  title: string;
  text: string;
}

interface Answer {
  items: Notice[];
  /** Через сколько секунд карточка уезжает сама. 0 — ждать, пока закроют */
  seconds: number;
}

/** Длиннее — в карточку не влезет, и надо звать читать целиком. */
const SHORT = 90;

/** Текст без разметки: в карточке важна длина, а не оформление. */
function plain(text: string): string {
  return text.replace(/<[^>]*>/g, '').trim();
}

export function Notices({ ready }: { ready: boolean }) {
  const [items, setItems] = useState<Notice[]>([]);
  const [seconds, setSeconds] = useState(0);
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const [reading, setReading] = useState<Notice | null>(null);

  // Спрашиваем только когда действительно покажем: в режиме «один раз»
  // отметка ставится в момент выдачи, и тратить её впустую нельзя
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    request<Answer>('/api/notifications', {})
      .then((answer) => {
        if (!alive) return;
        setItems(answer.items ?? []);
        setSeconds(answer.seconds ?? 0);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [ready]);

  const close = (id: string) => {
    setLeaving((was) => new Set(was).add(id));
    setTimeout(() => setItems((was) => was.filter((item) => item.id !== id)), 260);
  };

  return (
    <>
      {items.length > 0 && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-70 flex flex-col items-center gap-2 px-3 pt-[calc(10px+env(safe-area-inset-top,0px))]">
          {items.map((item, index) => (
            <Card
              key={item.id}
              notice={item}
              seconds={seconds}
              delay={index * 140}
              leaving={leaving.has(item.id)}
              onClose={() => close(item.id)}
              onRead={() => {
                tap();
                setReading(item);
                close(item.id);
              }}
            />
          ))}
        </div>
      )}

      {reading && <Reader notice={reading} onClose={() => setReading(null)} />}
    </>
  );
}

function Card({
  notice,
  seconds,
  delay,
  leaving,
  onClose,
  onRead,
}: {
  notice: Notice;
  seconds: number;
  delay: number;
  leaving: boolean;
  onClose: () => void;
  onRead: () => void;
}) {
  const long = plain(notice.text).length > SHORT;
  const [shift, setShift] = useState(0);
  const start = useRef<number | null>(null);
  const moved = useRef(false);

  // Сама уезжает — но только если админ задал срок. Ноль означает «висит,
  // пока не закроют»: бывают сообщения, которые нельзя пролистать мимо.
  useEffect(() => {
    if (!seconds || leaving) return;
    const timer = setTimeout(onClose, seconds * 1000 + delay);
    return () => clearTimeout(timer);
    // Таймер отсчитывается от появления карточки, а не от каждого рендера
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds, leaving, delay]);

  /* Смахивание вверх. Тянем только вверх: карточка приехала оттуда, туда же и
     уходит — движение обратимо и предсказуемо. */
  const onDown = (event: React.PointerEvent) => {
    start.current = event.clientY;
    moved.current = false;
  };

  const onMove = (event: React.PointerEvent) => {
    if (start.current === null) return;
    const delta = Math.min(0, event.clientY - start.current);
    if (delta < -4) moved.current = true;
    setShift(delta);
  };

  const onUp = () => {
    if (start.current === null) return;
    start.current = null;
    if (shift < -44) {
      tap();
      onClose();
      return;
    }
    setShift(0);
    // Не тянули, а нажали — это просьба прочитать целиком
    if (!moved.current) onRead();
  };

  return (
    <div
      role="status"
      style={{
        animationDelay: leaving ? undefined : `${delay}ms`,
        transform: shift ? `translateY(${shift}px)` : undefined,
        opacity: shift ? Math.max(0, 1 + shift / 90) : undefined,
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      className={[
        'pointer-events-auto w-full max-w-[440px] cursor-pointer overflow-hidden rounded-layer',
        'glass wet-edge layer-shadow touch-none bg-night-2/[.88] backdrop-blur-xl',
        shift ? 'notice-drag' : leaving ? 'notice-out' : 'notice',
      ].join(' ')}
    >
      <div className="flex items-start gap-3 p-3.5">
        {notice.icon && (
          <span className="grid size-9 shrink-0 place-items-center rounded-[11px] bg-mint/12 text-lg">
            {notice.icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {notice.title && (
            <div className="text-[15px] font-[680] tracking-[-.01em]">{notice.title}</div>
          )}
          {notice.text && (
            <div
              className={[
                'mt-0.5 text-[13px] leading-snug break-words text-hint',
                long ? 'line-clamp-2' : '',
              ].join(' ')}
            >
              {renderText(notice.text)}
            </div>
          )}
          {long && (
            <span className="mt-1.5 inline-block rounded-full bg-mint/12 px-2.5 py-1 text-[11px] font-[650] text-mint">
              Подробнее
            </span>
          )}
        </div>
        {/* Стрелка вверх подсказывает, куда смахивать: закрыть по-прежнему
            можно, но теперь это отдельное движение, а не случайный тап */}
        <span aria-hidden className="mt-1 shrink-0 text-[11px] text-hint/50">
          ↑
        </span>
      </div>

      {/* Полоска дожития: видно, сколько карточка ещё повисит */}
      {seconds > 0 && !leaving && !shift && (
        <div
          className="notice-life h-[2px] bg-mint/70"
          style={{ animationDuration: `${seconds}s`, animationDelay: `${delay}ms` }}
        />
      )}
    </div>
  );
}

/** Полный текст: висит, пока не закроют, — читать никто не должен спешить. */
function Reader({ notice, onClose }: { notice: Notice; onClose: () => void }) {
  return (
    <div className="gate-veil" onClick={onClose}>
      <div
        className="gate-card glass wet-edge layer-shadow w-full max-w-[400px] rounded-layer p-5 backdrop-blur-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          {notice.icon && (
            <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-mint/12 text-xl">
              {notice.icon}
            </span>
          )}
          {notice.title && (
            <h2 className="min-w-0 flex-1 pt-1 text-[17px] font-[700] tracking-[-.02em]">
              {notice.title}
            </h2>
          )}
        </div>

        <div className="mt-3 max-h-[55vh] overflow-y-auto text-[15px] leading-relaxed break-words">
          {renderText(notice.text)}
        </div>

        <button
          type="button"
          onClick={() => {
            tap();
            onClose();
          }}
          className="accent-glow mt-4 w-full rounded-xl bg-linear-135 from-mint to-mint-deep px-4 py-3 text-sm font-[650] text-mint-ink transition-transform duration-150 ease-glass active:scale-[.97]"
        >
          Понятно
        </button>
      </div>
    </div>
  );
}
